/**
 * Flags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const flagsPack: ToilePack = {
    slug: 'flags',
    category: 'motif',
    name: 'Flags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'pennant', label: 'Pennant on a pole', subject: "A single triangular pennant in two colours fluttering on a tall white pole against a plain sky, the fabric's fold caught in the wind."},
        {key: 'bunting', label: 'Bunting', subject: 'A string of small rectangular bunting flags in six colours hung in two shallow swags across a plain ground, a few flags lifted by wind.'},
        {key: 'checkered', label: 'Checkered flag', subject: "A black-and-white checkered flag waving on a short pole, the checks distorting in the fabric's folds, a plain ground."},
    ],
    styles: [
        {key: 'flat', label: 'Flat', style: 'flat vector illustration, bold simplified shapes, limited palette, plain ground', palette: 'two bold flat colours, no gradients', framing: 'centred on a plain white background with generous padding around the shape'},
        {key: 'painted', label: 'Painted', style: 'painted illustration, fabric texture, soft light, textured canvas', palette: 'warm painted colour with visible canvas texture', framing: 'set on a plain background, the fabric catching a soft light'},
        {key: 'line', label: 'Line drawing', style: 'single-weight black line drawing, clean contour, white ground', palette: 'black ink only, no other colour', framing: 'centred on a plain white background with a wide even margin'},
    ],
    directions: ['a pirate flag instead', 'in rainbow colours', 'for a party banner'],
    directionHint: 'try: a pirate flag instead',
    seo: {
        title: 'Flag art: pennant, bunting, checkered flag',
        metaDescription: "Nine flag illustrations — a fluttering pennant, bunting and a checkered race flag — in flat, painted and line-drawing styles, not a country's colours.",
        h1: 'Flag drawings: pennant, bunting, checkered in three styles',
        tagline: 'Nine renders, three flag readings, three looks each — pennant to checkered.',
        intro: "The flag as an object, not a country's colours: a triangular pennant fluttering on a pole, a string of bunting in six colours, and a black-and-white checkered race flag waving. Each renders flat and bold, in painted fabric texture, or as a single black line. Ask for a pirate flag or a party-banner crop and the same three looks carry it.",
        useCases: [
            'a party or birthday banner design',
            'a race or motorsport print',
            'a bunting or garland design',
            'a social media event graphic',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed in a wide landscape crop, suited to a banner, a print or a poster.'},
            {q: 'Can I get a pirate flag or rainbow colours?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Flat is two bold colours on white, painted shows visible fabric-canvas texture, and line drawing is a single black contour.'},
            {q: "Are these a specific country's flag?", a: "No — these are the flag as an object (pennant, bunting, checkered), not any nation's colours."},
        ],
    },
}
